import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import { ProjectTask, TaskType } from "../../../../interfaces/ProjectTask";
import { epicColor } from "../../../../utils/epicColor";

interface Line {
  date: string;
  plannedHours: number;
}

interface Props {
  tasks: ProjectTask[];
  schedules?: Record<number, Line[]>;
  employeesCapacity?: Record<number, number>;
  title?: string;
  showProject?: boolean;
  /** Portal do cliente: sem responsável, horas nem capacity (ver TaskGantt). */
  modoCliente?: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const pad = (n: number) => String(n).padStart(2, "0");
const parseKey = (k: string) => {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const dateKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * DAY_MS);
const isoMonday = (d: Date) => {
  const date = startOfDay(d);
  const diff = (date.getDay() + 6) % 7;
  return new Date(date.getTime() - diff * DAY_MS);
};
const fmtDM = (d: Date) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;

const SEGMENT_GAP = 3;
interface Segment {
  start: Date;
  end: Date;
  dates: string[];
  total: number;
}
const buildSegments = (lines: Line[]): Segment[] => {
  const sorted = lines.filter((l) => l.plannedHours > 0).slice().sort((a, b) => (a.date < b.date ? -1 : 1));
  const segs: Segment[] = [];
  let cur: Line[] = [];
  let prev: Date | null = null;
  const flush = () => {
    if (!cur.length) return;
    const dates = cur.map((l) => l.date);
    segs.push({ start: parseKey(dates[0]), end: parseKey(dates[dates.length - 1]), dates, total: cur.reduce((a, l) => a + l.plannedHours, 0) });
    cur = [];
  };
  for (const l of sorted) {
    const d = parseKey(l.date);
    if (prev && (d.getTime() - prev.getTime()) / DAY_MS > SEGMENT_GAP) flush();
    cur.push(l);
    prev = d;
  }
  flush();
  return segs;
};

const barHex = (task: ProjectTask) => {
  switch (task.scheduleState) {
    case "overdue":
      return "#ef4444";
    case "due_soon":
      return "#f59e0b";
    case "done":
      return "#22c55e";
    case "cancelled":
      return "#9ca3af";
    default:
      return "#1466E3";
  }
};

// Página A4 paisagem.
const PAGE_W = 841.89;
const MARGIN = 22;
const NAME_W = 170;
const ROW_H = 16;
const HEADER_H = 14;

const styles = StyleSheet.create({
  page: { fontFamily: "Helvetica", fontSize: 8, paddingTop: MARGIN, paddingHorizontal: MARGIN, paddingBottom: 28, color: "#1b2559" },
  title: { fontSize: 13, fontFamily: "Helvetica-Bold", marginBottom: 2 },
  sub: { fontSize: 8, color: "#707eae", marginBottom: 8 },
  headRow: { flexDirection: "row" },
  nameCell: { width: NAME_W, paddingRight: 6 },
  row: { flexDirection: "row", alignItems: "center", borderTopWidth: 0.5, borderTopColor: "#eef0f4" },
  legend: { flexDirection: "row", marginTop: 8 },
  legendItem: { flexDirection: "row", alignItems: "center", marginRight: 12 },
  legendSwatch: { width: 7, height: 7, borderRadius: 1, marginRight: 3 },
  legendText: { fontSize: 7, color: "#707eae" },
});

const GanttPdf: React.FC<Props> = ({ tasks, schedules, employeesCapacity, title = "Cronograma", showProject, modoCliente = false }) => {
  const linesOf = (t: ProjectTask): Line[] => schedules?.[t.id] ?? [];

  // Árvore (épico → tarefa → subtarefa).
  const childrenOf = new Map<number, ProjectTask[]>();
  const byId = new Map<number, ProjectTask>();
  tasks.forEach((t) => byId.set(t.id, t));
  tasks.forEach((t) => {
    if (t.parentTaskId != null) {
      const arr = childrenOf.get(t.parentTaskId) ?? [];
      arr.push(t);
      childrenOf.set(t.parentTaskId, arr);
    }
  });
  const order = (a: ProjectTask, b: ProjectTask) => a.orderIndex - b.orderIndex || a.id - b.id;
  childrenOf.forEach((arr) => arr.sort(order));
  const topLevel = tasks.filter((t) => t.parentTaskId == null).sort(order);
  const isLeaf = (t: ProjectTask) => (childrenOf.get(t.id)?.length ?? 0) === 0;

  const epicIdOf = (t: ProjectTask): number | null => {
    let cur: ProjectTask | undefined = t;
    let g = 0;
    while (cur && g++ < 12) {
      if (cur.type === TaskType.Epic) return cur.id;
      if (cur.parentTaskId == null) return null;
      cur = byId.get(cur.parentTaskId);
    }
    return null;
  };

  const hasSchedule = (t: ProjectTask) => linesOf(t).length > 0 || !!t.startDate || !!t.dueDate;

  // Planejado por colaborador/dia — só folhas (overlay de capacity).
  const plannedByEmpDay = new Map<string, number>();
  tasks.forEach((t) => {
    if (t.assigneeEmployeeId == null || !isLeaf(t)) return;
    linesOf(t).forEach((l) => {
      const k = `${t.assigneeEmployeeId}|${l.date}`;
      plannedByEmpDay.set(k, (plannedByEmpDay.get(k) ?? 0) + l.plannedHours);
    });
  });
  const dailyCapOf = (id?: number | null) => {
    const weekly = (id != null ? employeesCapacity?.[id] : undefined) ?? 40;
    return weekly > 0 ? weekly / 5 : 8;
  };

  // Span (datas) de uma folha; envelope agregando descendentes p/ pais.
  const leafSpan = (t: ProjectTask): { min: number; max: number } | null => {
    const lines = linesOf(t);
    if (lines.length) {
      const ms = lines.map((l) => parseKey(l.date).getTime());
      return { min: Math.min(...ms), max: Math.max(...ms) };
    }
    if (t.startDate || t.dueDate) {
      const s = startOfDay(new Date(t.startDate || t.dueDate!)).getTime();
      const e = startOfDay(new Date(t.dueDate || t.startDate!)).getTime();
      return { min: Math.min(s, e), max: Math.max(s, e) };
    }
    return null;
  };
  const spanOf = (taskId: number): { start: Date; end: Date } | null => {
    let min = Infinity;
    let max = -Infinity;
    const collect = (id: number) => {
      const kids = childrenOf.get(id) ?? [];
      if (kids.length === 0) {
        const node = byId.get(id);
        const s = node ? leafSpan(node) : null;
        if (s) {
          min = Math.min(min, s.min);
          max = Math.max(max, s.max);
        }
      } else kids.forEach((k) => collect(k.id));
    };
    collect(taskId);
    if (!isFinite(min)) return null;
    return { start: new Date(min), end: new Date(max) };
  };

  // Nós visíveis: com cronograma + ancestrais.
  const visible = new Set<number>();
  tasks.filter(hasSchedule).forEach((t) => {
    visible.add(t.id);
    let p = t.parentTaskId ?? null;
    let g = 0;
    while (p != null && g++ < 12) {
      visible.add(p);
      p = byId.get(p)?.parentTaskId ?? null;
    }
  });

  const rows: { task: ProjectTask; depth: number; parent: boolean }[] = [];
  const walk = (node: ProjectTask, depth: number) => {
    if (!visible.has(node.id)) return;
    const kids = (childrenOf.get(node.id) ?? []).filter((k) => visible.has(k.id));
    rows.push({ task: node, depth, parent: kids.length > 0 });
    kids.forEach((k) => walk(k, depth + 1));
  };
  topLevel.forEach((t) => walk(t, 0));

  // Intervalo total a partir das folhas visíveis.
  let min = Infinity;
  let max = -Infinity;
  rows.forEach(({ task, parent }) => {
    if (parent) return;
    const s = leafSpan(task);
    if (s) {
      min = Math.min(min, s.min);
      max = Math.max(max, s.max);
    }
  });

  const empty = !isFinite(min);
  const rangeStart = empty ? startOfDay(new Date()) : isoMonday(new Date(min));
  const rangeEnd = empty ? addDays(rangeStart, 7) : addDays(startOfDay(new Date(max)), 2);
  const totalDays = Math.max(1, Math.round((rangeEnd.getTime() - rangeStart.getTime()) / DAY_MS) + 1);
  const timelineW = PAGE_W - 2 * MARGIN - NAME_W;
  const ppd = timelineW / totalDays;
  const offsetOf = (d: Date) => (startOfDay(d).getTime() - rangeStart.getTime()) / DAY_MS;

  const weekCols: { left: number; label: string }[] = [];
  for (let w = new Date(rangeStart); w.getTime() <= rangeEnd.getTime(); w = addDays(w, 7)) {
    weekCols.push({ left: offsetOf(w) * ppd, label: fmtDM(w) });
  }

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page} wrap>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.sub}>
          {empty ? "Sem tarefas com cronograma." : `Período: ${fmtDM(rangeStart)} a ${fmtDM(addDays(rangeEnd, -2))}`}
        </Text>

        <View style={styles.headRow} fixed>
          <View style={styles.nameCell}>
            <Text style={{ fontSize: 7, color: "#707eae" }}>Tarefa</Text>
          </View>
          <View style={{ width: timelineW, height: HEADER_H, position: "relative" }}>
            {weekCols.map((c, i) => (
              <Text key={i} style={{ position: "absolute", left: c.left, top: 2, fontSize: 6, color: "#a3aed0" }}>
                {c.label}
              </Text>
            ))}
          </View>
        </View>

        {rows.map(({ task, depth, parent }) => {
          const accent = epicColor(epicIdOf(task));
          const dailyCap = dailyCapOf(task.assigneeEmployeeId);

          // Pai/épico: barra-envelope (cor do épico). Folha: segmentos do cronograma.
          const span = parent ? spanOf(task.id) : null;
          const segments: Segment[] = !parent
            ? linesOf(task).length
              ? buildSegments(linesOf(task))
              : leafSpan(task)
              ? [
                  {
                    start: startOfDay(new Date(task.startDate || task.dueDate!)),
                    end: startOfDay(new Date(task.dueDate || task.startDate!)),
                    dates: [],
                    total: modoCliente ? 0 : task.estimatedHours ?? 0,
                  },
                ]
              : []
            : [];

          const overDays: number[] = [];
          if (!modoCliente && !parent && task.assigneeEmployeeId != null && linesOf(task).length) {
            for (let i = 0; i < totalDays; i++) {
              const dk = dateKey(addDays(rangeStart, i));
              if ((plannedByEmpDay.get(`${task.assigneeEmployeeId}|${dk}`) ?? 0) > dailyCap + 0.001) overDays.push(i);
            }
          }

          return (
            <View key={task.id} style={[styles.row, { height: ROW_H }]} wrap={false}>
              <View style={styles.nameCell}>
                <View style={{ flexDirection: "row", alignItems: "center", paddingLeft: depth * 9 }}>
                  <View style={{ width: 5, height: 5, borderRadius: 1, backgroundColor: accent.solid, marginRight: 3 }} />
                  <Text
                    style={{ fontSize: 8, fontFamily: parent ? "Helvetica-Bold" : "Helvetica" }}
                  >
                    {task.title}
                  </Text>
                </View>
                {!parent && (modoCliente ? showProject && task.projectName : true) && (
                  <Text style={{ fontSize: 6.5, color: "#707eae", marginLeft: 8 }}>
                    {modoCliente
                      ? task.projectName
                      : showProject && task.projectName
                      ? `${task.projectName} · ${task.assigneeName ?? "Sem responsável"}`
                      : task.assigneeName ?? "Sem responsável"}
                  </Text>
                )}
              </View>

              <View style={{ width: timelineW, height: ROW_H, position: "relative" }}>
                {/* Pai: envelope */}
                {parent && span && (
                  <View
                    style={{
                      position: "absolute",
                      left: offsetOf(span.start) * ppd,
                      top: ROW_H / 2 - 2,
                      width: Math.max(4, ((span.end.getTime() - span.start.getTime()) / DAY_MS + 1) * ppd),
                      height: 4,
                      backgroundColor: accent.solid,
                      opacity: 0.5,
                      borderRadius: 2,
                    }}
                  />
                )}

                {/* Folha: faixas de capacity */}
                {!parent &&
                  overDays.map((i) => (
                    <View
                      key={`ov-${i}`}
                      style={{ position: "absolute", left: i * ppd, top: 0, width: ppd, height: ROW_H, backgroundColor: "#fde2e2" }}
                    />
                  ))}

                {/* Folha: segmentos */}
                {!parent &&
                  segments.map((seg, si) => {
                    const spanDays = Math.round((seg.end.getTime() - seg.start.getTime()) / DAY_MS) + 1;
                    return (
                      <View
                        key={si}
                        style={{
                          position: "absolute",
                          left: offsetOf(seg.start) * ppd,
                          top: 3,
                          width: Math.max(3, spanDays * ppd),
                          height: ROW_H - 6,
                          backgroundColor: barHex(task),
                          borderRadius: 2,
                          justifyContent: "center",
                          paddingHorizontal: 2,
                        }}
                      >
                        {seg.total > 0 && spanDays * ppd > 14 ? (
                          <Text style={{ fontSize: 6, color: "#fff" }}>{seg.total}h</Text>
                        ) : null}
                      </View>
                    );
                  })}
              </View>
            </View>
          );
        })}

        <View style={styles.legend} fixed>
          {[
            { c: "#1466E3", t: "No prazo" },
            { c: "#f59e0b", t: "Vence em breve" },
            { c: "#ef4444", t: "Atrasada" },
            { c: "#22c55e", t: "Concluída" },
            { c: "#fde2e2", t: "Acima do capacity" },
          ]
            .filter((l) => !modoCliente || l.t !== "Acima do capacity")
            .map((l, i) => (
            <View key={i} style={styles.legendItem}>
              <View style={[styles.legendSwatch, { backgroundColor: l.c }]} />
              <Text style={styles.legendText}>{l.t}</Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default GanttPdf;
